
import React, { useState } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import { Button } from '../components/ui/Button';
import { Microscope, AlertCircle, Zap } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface AuthPageProps {
  type: 'login' | 'signup';
}

const AuthPage: React.FC<AuthPageProps> = ({ type }) => {
  const navigate = useNavigate();
  const user = useAppStore(state => state.user);
  const isAuthInitialized = useAppStore(state => state.isAuthInitialized);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect if already logged in
  if (isAuthInitialized && user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (type === 'login') {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
        });
        if (error) throw error;
        alert('Verification email sent! Check your inbox.');
      }
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'An error occurred during authentication.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <div className="p-8 w-full max-w-md">
        <div className="flex flex-col items-center mb-10 text-center">
            <Link to="/" className="hover:opacity-80 transition-opacity flex flex-col items-center">
              <Microscope className="h-16 w-16 text-primary-500 mb-6" strokeWidth={1.5} />
              <span className="text-xl font-semibold text-slate-900 tracking-tight">HistologyPro</span>
            </Link>
            <h2 className="text-3xl font-black text-slate-900 tracking-tight mt-4">
              {type === 'login' ? 'Sign In' : 'Join the Community'}
            </h2>
            <p className="text-slate-500 mt-2 text-sm font-medium">
                {type === 'login' 
                  ? 'Access your clinical histology library.' 
                  : 'Start your journey to mastery today.'}
            </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3 text-red-600 text-sm">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off">
          <div>
            <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Email</label>
            <input
              name="histology_auth_email"
              type="email"
              required
              autoComplete="none"
              className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all font-medium"
              placeholder="" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Password</label>
            <input
              name="histology_auth_password"
              type="password"
              required
              autoComplete="new-password"
              className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all font-medium"
              placeholder=""
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          
          <Button type="submit" className="w-full h-14 text-sm font-black uppercase tracking-[0.2em] rounded-2xl shadow-xl shadow-primary-500/20" isLoading={isLoading}>
            {type === 'login' ? 'Enter Dashboard' : 'Create Account'}
          </Button>
        </form>

        <div className="mt-8 text-center space-y-4">
          {type === 'login' ? (
            <p className="text-slate-500 font-medium text-sm">
              New here?{' '}
              <Link to="/signup" className="text-primary-600 hover:text-primary-700 font-bold">
                Join now
              </Link>
            </p>
          ) : (
            <p className="text-slate-500 font-medium text-sm">
              Have an account?{' '}
              <Link to="/login" className="text-primary-600 hover:text-primary-700 font-bold">
                Sign in
              </Link>
            </p>
          )}

          <div className="pt-2">
            <Link to="/upload" className="inline-flex items-center text-xs font-black text-slate-400 uppercase tracking-[0.2em] hover:text-primary-500 transition-colors">
              <Zap className="h-3 w-3 mr-2" /> Skip for now & Try Guest Mode
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
